/**
 * Welk teken bij een melding hoort.
 *
 * ------------------------------------------------------------------
 * Waarom dit niet aan de kleur hangt
 * ------------------------------------------------------------------
 * MARKS (IntelligenceDetail.js) kent negen tonen, en die blijven: ze staan op
 * de rijen, de typechips, de legenda én in de Android-app. Maar één van die
 * negen — `werk` — dekt wegwerkzaamheden, een dichte rijstrook, een afgesloten
 * weg én een open brug. Vier dingen die je verschillend behandelt: omrijden,
 * invoegen, wachten. Een teken dat aan de tóón hangt, zegt daar niets nieuws
 * over.
 *
 * Dus: het teken hangt aan het TYPE, de kleur aan de DIENST. Samen zeggen ze
 * meer dan elk apart.
 *
 * De controles delen bewust één schild. Daar dóét de kleur het werk — blauw is
 * politie, cyaan is taxi — en vijf verschillende uniformpetjes zijn op twaalf
 * pixel vijf vlekken. ILT is de uitzondering en krijgt een vrachtwagen, want
 * dat gaat over zwaar verkeer en niet over wie er staat.
 *
 * ------------------------------------------------------------------
 * Waarom de vorm als tekst
 * ------------------------------------------------------------------
 * Dezelfde tekens worden op twee manieren getekend: door Leaflet, dat een HTML-
 * string wil (L.divIcon), en door React op de bordrij. Twee kopieën van
 * dezelfde vorm lopen uiteen — dat is precies hoe de kaart en de lijst ooit
 * andere dingen gingen zeggen. Eén bron, twee lezers.
 */

/**
 * De vormen, in een vak van 16×16. Lijnen en geen vlakken: op twaalf pixel is
 * een gevulde vorm een vlek.
 *
 * Alles is pad-tekst, ook de cirkels en de rechthoek die dat niet hoefden te
 * zijn. Reden: Android leest deze strings met androidx PathParser en iOS met
 * zijn eigen lezer, en dat kan alleen als er één vorm van opschrijven is. Een
 * <circle> zou een tweede tabel afdwingen, en twee tabellen van dezelfde vorm
 * lopen uiteen — dat is precies hoe de kaart en de lijst ooit andere dingen
 * gingen zeggen.
 */
export const GLYPH = {
  /** Een camerahuis met lens. Vaste, mobiele en telefoonflitsers. */
  cam: [
    "M2 6.5h12v6H2z",
    "M5.9 9.5c0 -1.16 0.94 -2.1 2.1 -2.1c1.16 0 2.1 0.94 2.1 2.1c0 1.16 -0.94 2.1 -2.1 2.1c-1.16 0 -2.1 -0.94 -2.1 -2.1z",
    "M4.5 6.5l1-2h3l1 2",
  ],
  /** Twee palen met de meetafstand ertussen. */
  traj: ["M3.5 3.5v9", "M12.5 3.5v9", "M6 8h4"],
  /** Een lamp op een paal. */
  rood: [
    "M5 2h6v9H5z",
    "M6.9 4.6c0 -0.608 0.492 -1.1 1.1 -1.1c0.608 0 1.1 0.492 1.1 1.1c0 0.608 -0.492 1.1 -1.1 1.1c-0.608 0 -1.1 -0.492 -1.1 -1.1z",
    "M8 11v3",
  ],
  /** Het schild: iemand staat te controleren. De kleur zegt wie. */
  schild: ["M8 2l5 2v4.5c0 3-2.2 5.2-5 6-2.8-.8-5-3-5-6V4z"],
  /** Een vrachtwagen. Alleen ILT: dat gaat over zwaar verkeer. */
  truck: [
    "M1.6 4.6h7v5.2h-7z",
    "M8.6 6.4h2.6l2.2 2.1v1.3h-4.8z",
    "M3.1 11.4c0 -0.718 0.582 -1.3 1.3 -1.3c0.718 0 1.3 0.582 1.3 1.3c0 0.718 -0.582 1.3 -1.3 1.3c-0.718 0 -1.3 -0.582 -1.3 -1.3z",
    "M9.7 11.4c0 -0.718 0.582 -1.3 1.3 -1.3c0.718 0 1.3 0.582 1.3 1.3c0 0.718 -0.582 1.3 -1.3 1.3c-0.718 0 -1.3 -0.582 -1.3 -1.3z",
  ],
  /**
   * De kegel. Smal en op een brede voet — met opzet.
   *
   * Hij stond eerst op een gelijkzijdige driehoek, en was daarmee op twaalf
   * pixel niet te onderscheiden van `waarschuwing` hieronder. Twee van de
   * vaakst voorkomende soorten, dezelfde speld. Een driehoek zonder voet is een
   * waarschuwing; een driehoek mét voet is een ding dat er staat.
   */
  kegel: ["M8 2.2L10.9 11H5.1z", "M2.6 13.4h10.8", "M6.6 7.4h2.8"],
  /** Invoegen. Een rijstrook dicht is iets anders dan werk aan de weg. */
  invoegen: ["M4 14V8.5c0-2 1.8-3.4 4-3.4", "M12 3v9", "M10.4 4.6L12 3l1.6 1.6"],
  /** Een slagboom met strepen. Je kunt er niet langs. */
  dicht: [
    "M1.8 6.2h12.4v3.6H1.8z",
    "M4.4 6.2l-2 3.6", "M8 6.2l-2 3.6", "M11.6 6.2l-2 3.6",
  ],
  /** Een brug. Wachten, geen omrijden. */
  brug: ["M2 12V5", "M14 12V5", "M2 9.4c3.2-3.4 8.8-3.4 12 0", "M1 13.6h14"],
  /** Een klap. Geen waarschuwing maar iets dat gebeurd is. */
  klap: [
    "M8 1.8v3", "M8 11.2v3", "M1.8 8h3", "M11.2 8h3",
    "M3.9 3.9l2.1 2.1", "M10 10l2.1 2.1", "M12.1 3.9L10 6", "M6 10l-2.1 2.1",
  ],
  /** Let op: pech, een object op de weg, een gevaarlijke situatie. */
  waarschuwing: ["M8 2.2l5.6 9.7H2.4z", "M8 6.2v2.6", "M8 10.5v.1"],
};

/** Per soort, waar de soort iets toevoegt dat de dienstkleur niet zegt. */
const PER_TYPE = {
  trajectcontrole: "traj",
  roodlichtcamera: "rood",
  rijstrook_dicht: "invoegen",
  wegafsluiting: "dicht",
  brug_open: "brug",
  ongeval: "klap",
};

/** En anders per dienst. Zie de kop: de kleur doet daar het werk. */
const PER_TONE = {
  flits: "cam",
  werk: "kegel",
  sit: "waarschuwing",
  ilt: "truck",
  pol: "schild",
  kmar: "schild",
  dou: "schild",
  bel: "schild",
  taxi: "schild",
};

/**
 * Het teken voor één melding.
 *
 * @param authority de soort, zoals de server hem stuurt
 * @param tone      de toon uit authorityMark() — de dienst, in één woord
 */
export const glyphFor = (authority, tone) =>
  PER_TYPE[authority] || PER_TONE[tone] || "schild";

/** De paden zelf, of het schild als terugval. Nooit leeg. */
export const glyphPaths = (id) => GLYPH[id] || GLYPH.schild;

/** Diezelfde paden als SVG-opmaak, voor wie tekent in HTML. */
export const glyphShape = (id) =>
  glyphPaths(id).map((d) => `<path d="${d}"/>`).join("");

/**
 * Eén speld, als HTML-string voor L.divIcon.
 *
 * Donker met een dunne gekleurde ring en het teken in diezelfde kleur. Geen
 * gevulde bol: die trekt het oog van de kaart af en dekt precies de straat waar
 * hij iets over zegt.
 */
export const markerHtml = ({ shape, colour, size = 23, faded = false }) => `
  <span style="
    display:grid; place-items:center; width:${size}px; height:${size}px;
    border-radius:50%; background:rgba(8,12,17,.86);
    border:1.5px solid ${colour};
    ${faded ? "" : `box-shadow:0 0 0 3px ${colour}1f, 0 1px 5px rgba(0,0,0,.55);`}
    opacity:${faded ? 0.42 : 1};
  ">
    <svg viewBox="0 0 16 16" width="${Math.round(size * 0.52)}" height="${Math.round(size * 0.52)}"
         fill="none" stroke="${colour}" stroke-width="1.6"
         stroke-linecap="round" stroke-linejoin="round">${shape}</svg>
  </span>`;
